/**
 * @file: utils.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { PresentationTheme } from '@euler/app/tv/theme/types';
import { DefectiveLevel, DefectiveLevelColorMap } from '@euler/model';
import { InspectionTaskJobStatus } from '@euler/model/enum';
import addMonths from 'date-fns/addMonths';
import differenceInMonths from 'date-fns/differenceInMonths';
import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import {
  MaintenancePlanDisplayInfo,
  MaintenancePlanItem,
  MaintenancePlanStartParams,
  MaintenancePlanValueParams,
} from './types';

export const getDisplayInfo = (
  theme: PresentationTheme,
  colorMap: DefectiveLevelColorMap,
  value: MaintenancePlanValueParams | undefined,
  item: MaintenancePlanItem,
): MaintenancePlanDisplayInfo => {
  if (
    value == null ||
    value.mileage === item.startParams.currentVehicleMileage
  ) {
    if (item.constructionJob == null) {
      return {
        type: 'immediate',
        status: 'none',
        label: '建议本次保养/维修',
        color: colorMap[DefectiveLevel.Urgent],
      };
    } else if (
      item.constructionJob.status === InspectionTaskJobStatus.Pending
    ) {
      return {
        type: 'immediate',
        status: 'pending',
        label: '已安排施工计划',
        color: theme.task.maintenancePlan.confirmedLabelTextColor,
      };
    } else if (
      item.constructionJob.status === InspectionTaskJobStatus.InProgress
    ) {
      return {
        type: 'immediate',
        status: 'pending',
        label: '正在进行施工',
        color: theme.task.maintenancePlan.confirmedLabelTextColor,
      };
    } else {
      return {
        type: 'immediate',
        status: 'pending',
        label: '本次保养已完成',
        color: theme.task.maintenancePlan.confirmedLabelTextColor,
      };
    }
  } else {
    const isConfirmed =
      item.planDetail != null &&
      value?.mileage != null &&
      item.planDetail.nextServiceMileage === value.mileage;
    const [year, month] = [getYear(value.date), getMonth(value.date) + 1];
    const label = [
      isConfirmed ? '已预约' : '预计',
      year === getYear(new Date()) ? '今年' : `${year}年`,
      `${month}月保养\n`,
      `(约${value.mileage}公里)`,
    ].join('');
    return {
      type: 'deferred',
      status: isConfirmed ? 'confirmed' : 'none',
      label,
      color: isConfirmed
        ? theme.task.maintenancePlan.confirmedLabelTextColor
        : theme.task.maintenancePlan.slider.thumbStartColorPending,
    };
  }
};

export const maintenancePlanDateByMileage = (
  mileage: number,
  startParams: MaintenancePlanStartParams,
) => {
  const avgMileagePerYear =
    startParams.currentVehicleMileage / startParams.currentVehicleAge;
  const offset = mileage - startParams.currentVehicleMileage;
  const m = Math.round((offset / avgMileagePerYear) * 12);
  return addMonths(startParams.startDate, m);
};

export const maintenancePlanMileageByDate = (
  date: Date,
  startParams: MaintenancePlanStartParams,
) => {
  const avgMileagePerYear =
    startParams.currentVehicleMileage / startParams.currentVehicleAge;
  const m = differenceInMonths(date, startParams.startDate);
  return Math.round(
    startParams.currentVehicleMileage + avgMileagePerYear * (m / 12),
  );
};
